
import { Component, Vue, Watch } from "vue-property-decorator"
import { Action, Getter, Mutation, State } from "vuex-class"

import User, { CurrentUser, LabeledValue, UserWithFields } from "@/types/user"
import { DataDefinitionWithFields, FieldInstance } from "@/types"

import UserProfilePicture from "@/components/Account/UserProfilePicture.vue"
import DataForm from "@/components/Config/DataForm.vue"
import StepProgress from "@/components/Login/StepProgress.vue"
import TenantInfoDisplay from "@/components/Info/TenantInfoDisplay.vue"

import api from "@/services/api"
import tenantApi from "@/services/api_Tenant"
import TenantInfo from "@/types/tenantInfo"

enum IntroStepTypes {
	Welcome,
	UserDetails,
	TenantInfo
}

interface IntroStep {
	title: string
	type: IntroStepTypes
	section?: string | null
}

@Component({
	components: {
		"user-profile-picture": UserProfilePicture,
		"data-form": DataForm,
		"step-progress": StepProgress,
		"tenant-info": TenantInfoDisplay
	}
})
export default class Introduction extends Vue {
	@Getter("getMe") public me!: CurrentUser

	@Action finishIntroduction!: () => void

	private userDetails: UserWithFields | null = null
	private userDefinition: DataDefinitionWithFields | null = null
	private userFields: { [key: string]: LabeledValue } = {}
	private tenantInfo: TenantInfo[] = []

	private IntroStepTypes = IntroStepTypes
	private currentStepIndex = 0
	private pageLoaded = false

	get userId () {
		return this.me ? this.me.id : null
	}

	get currentStep (): IntroStep {
		return this.availableSteps[this.currentStepIndex]
	}

	get definitionEditFields (): FieldInstance[] {
		return this.userDefinition && this.userDefinition.fields
			? this.userDefinition.fields.filter(f => f.allowSelfEdit)
			: []
	}

	get availableSteps (): IntroStep[] {
		const steps: IntroStep[] = [
			{ title: "Welcome", type: IntroStepTypes.Welcome }
		]

		if (this.definitionEditFields.length > 0) {
			const sections = this.definitionEditFields.map(f => f.section)
				.distinct()

			sections.forEach(s => {
				steps.push({
					title: "Details",
					type: IntroStepTypes.UserDetails,
					section: s
				})
			})
		} else {
			steps.push({
				title: "Details",
				type: IntroStepTypes.UserDetails,
				section: null
			})
		}

		// Add info if present
		if (this.tenantInfo && this.tenantInfo.length > 0) {
			steps.push({
				title: "Information",
				type: IntroStepTypes.TenantInfo,
				section: null
			})
		}

		return steps
	}

	get currentStepUserFields (): any[] {
		if (!this.userDefinition || !this.userDefinition.fields || !this.currentStep.section) return []
		return this.definitionEditFields
			.filter(f => f.section === this.currentStep.section)
			.sortBy("order")
			.map((field: FieldInstance) => {
				return {
					key: field.field.id + ".value",
					label: field.field.title,
					dataType: field.field.dataType,
					dataSourceId: field.field.dataSourceId,
					section: field.section,
					readonly: !!field.field.displayReadOnly,
					errors: (item: any) => !item || !field.requiredRecord || item[field.field.id!] ? null : "Please enter " + field.field.title,
					extensions: field.field.extensions
				}
			})
	}

	get isLastStep (): boolean {
		return this.currentStepIndex === this.availableSteps.length - 1
	}
	get isFirstStep (): boolean {
		return this.currentStepIndex === 0
	}

	async mounted () {
		if (this.me) {
			this.onUserChanged(this.me.id)
		}
	}

	@Watch("userId")
	async onUserChanged (userId: string | null) {
		this.pageLoaded = false

		if (userId) {
			await Promise.all([
				api.userSpecialDefinition().then(definition => this.userDefinition = definition),
				tenantApi.tenantInfoGet().then(info => this.tenantInfo = info),
				api.userFieldDetails(userId).then(userDetails => {
					this.userDetails = userDetails
					this.userFields = userDetails && userDetails.fieldValues ? userDetails.fieldValues : {}
				})
			])
		} else {
			this.userDetails = null
		}

		this.pageLoaded = true
	}

	/**
	 * @summary Handler which clears the picture URL in our user details.
	 */
	private onUserPictureUpdated () {
		this.userDetails!.pictureUrl = null
	}

	next () {
		this.currentStepIndex++
	}

	back () {
		this.currentStepIndex--
	}

	async finish () {
		this.pageLoaded = false // Hide page

		try {
			await api.saveOwnFields(this.userFields, true)
			this.finishIntroduction()
		} catch (err) {
			console.error(err)
			this.pageLoaded = true // Unhide page on error
		}
	}
}
